import React from 'react';
import PropTypes from 'prop-types';
import { Body } from '../styled components/Typography';
import { UIBar, UIBarGrowItem } from '../styled components/UIBar';

/**
 * The TableListItem component is used within the Table component
 * so the dividers between the items can be placed. You can pass on a left and/or right element,
 * that will be placed on the corresponding side of the option's label.
 */

const TableListItem = ({
  label,
  rightElement = null,
  leftElement = null,
  onClick = null,
  className = '',
  dataAttributes = {},
  disableKeyboardNavigation = false
}) => {
  const optionListeners = {};
  let accessibleAttributes = {};

  if (onClick) optionListeners.onClick = onClick;
  /** Make sure the onClick handler of list item can get executed
   *  when navigating with the keyboard and pressing enter   */
  if (onClick) {
    accessibleAttributes = {
      role: 'button',
      tabIndex: disableKeyboardNavigation ? null : 0
    };
    optionListeners.onKeyDown = (e) => e.keyCode !== 13 || onClick(e);
  }

  return (
    <>
      <UIBar
        className={className}
        tw="py-4 "
        data-test-type="table-list-item"
        {...optionListeners}
        {...accessibleAttributes}
        {...dataAttributes}
      >
        {leftElement}

        <div tw="truncate py-2">
          <Body tw="leading-32" as="span" $color="black">
            {label}
          </Body>
        </div>
        <UIBarGrowItem />
        {rightElement}
      </UIBar>
    </>
  );
};

TableListItem.propTypes = {
  label: PropTypes.string.isRequired,
  /** Element to the left side of the item */
  leftElement: PropTypes.element,
  /** Element to the right of the item */
  rightElement: PropTypes.element,
  /** Add an action to the click event of the item */
  onClick: PropTypes.func,
  /** ClassName is supported such that styled components can pass extra styling.
   * Do not use this to adjust the styling, because the ordering of the applied tailwind classes is undeterministic */
  className: PropTypes.string,
  dataAttributes: PropTypes.shape({}),
  /* If you have an onClick handler the TableListItem becomes focusable when navigating with the keyboard.
   When adding a left or right element thats also focusable and has the same handler attached
   you might want to avoid the additional navigation step.  */
  disableKeyboardNavigation: PropTypes.bool
};

export default TableListItem;
